import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useSelector, useDispatch } from "react-redux";
import toast from "react-hot-toast";
import DataTable from "react-data-table-component";

import { InputField } from "../../../../Components/formElements/InputField";
import FileUploadComponent from "../../../../Components/formElements/FileUploadComponent";
import ItemService from "../../../../services/customer_appCommonService";
import { getMRUserList } from "../../../../redux/feature/customer_app/customerSlice";

import {
  SERVICE_ACTION_OPTIONS,
  SERVICE_STATUS,
  JOB_STATUS,
  FINANCE_STATUS,
} from "./ServiceConstants";
import {
  getActiveDocument,
  computePcCallStatusOnCreate,
  buildServiceRequiredItems,
  buildSeAssignment,
} from "./serviceHelpers";

// ── Reusable Accordion Header ──
const AccordionHeader = ({ iconLabel, title, subtitle, isOpen, onToggle }) => (
  <h2 className="accordion-header">
    <Row className="accordion-button" style={{ cursor: "default" }}>
      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
        <i
          className={`bi ${isOpen ? "bi-chevron-right" : "bi-chevron-down"}`}
          onClick={onToggle}
          style={{ cursor: "pointer", fontSize: 16 }}
        />
      </Col>
      <Col xs={1} md={2} lg={2} className="text-center logoBtn">
        <span className="iconLogo">{iconLabel}</span>
      </Col>
      <Col xs={12} md={8} lg={8}>
        <span className="headerTitle">{title}</span>
        <span className="subHeaderTitle">{subtitle}</span>
      </Col>
      <Col xs={12} md={2} lg={2} className="headerButtonAlign" />
    </Row>
  </h2>
);

const CreateServiceModal = ({ show, onHide, erfData, onSuccess }) => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state?.user);
  const mrUserData = useSelector((state) => state?.customer?.mrUserList);

  // ── Form ───────────────────────────────────────────
  const [formData, setFormData] = useState({
    srNo: "",
    serviceDate: "",
    serviceTime: "",
    salePoint: "",
    deliveryPassTo: "",
    patientName: "",
    age: "",
    patientNo: "",
    emailId: "",
    // servicePassTo: "",
    userName: "",
    erfTypeCode: "",
    erfType: "",
    reno: "",
    erfDate: "",
    costCentre: "",
    caregiverName: "",
    caregiverRelation: "",
    caregiverNo1: "",
    caregiverNo2: "",
    challenges: "",
    billingAddress: "",
    deliveryAddress: "",
    orderType: "Sale & Rent",
    attachmentId: "",
    serviceCategory: "Service",
    serviceCategoryDescription: "Service",
    shouldCollectMoney: false,
    amountToCollect: 0,
    taxPercentage: 18,
  });

  const [rentItemsLocal, setRentItemsLocal] = useState([]);
  const [selectedSeId, setSelectedSeId] = useState("");
  const [selectedSeData, setSelectedSeData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Accordion toggles
  const [isFormOpen, setIsFormOpen] = useState(true);
  const [isItemsOpen, setIsItemsOpen] = useState(true);
  const [isMoneyOpen, setIsMoneyOpen] = useState(true);
  const [isAssignOpen, setIsAssignOpen] = useState(true);

  // Load SE list
  useEffect(() => {
    if (show) {
      dispatch(
        getMRUserList({
          page: 1,
          per_page: 999,
          queryName: "get_ServiceProvider_list",
          searchTerm: "",
          timeZone: "Asia/Calcutta",
        }),
      );
    }
  }, [show, dispatch]);

  // Pre-fill from ERF
  useEffect(() => {
    if (!show || !erfData) return;
    setFormData((prev) => ({
      ...prev,
      srNo: erfData.equipmentNr || "",
      serviceDate: new Date().toISOString().split("T")[0],
      serviceTime: new Date().toTimeString().slice(0, 5),
      salePoint: erfData.salePoint || "",
      deliveryPassTo: erfData.deliveryAddress || "",
      patientName: erfData.patientName || "",
      age: erfData.age || "",
      patientNo: erfData.mobileNr || "",
      emailId: erfData.email || "",
      // servicePassTo: erfData.salePoint || "",
      userName: erfData.createdBy || "",
      erfTypeCode: erfData.erfTypeCode || "",
      erfType: erfData.erfType || "",
      reno: erfData.documentNumber || "",
      erfDate: erfData.createdAt?.split("T")[0] || "",
      costCentre: erfData.costCenter || "",
      caregiverName: erfData.caregiverName || "",
      caregiverRelation: erfData.caregiverRelation || "",
      caregiverNo1: erfData.caregiverMobileNr || "",
      caregiverNo2: "",
      challenges: "",
      billingAddress: erfData.billingAddress || "",
      deliveryAddress: erfData.deliveryAddress || "",
      attachmentId: "",
      serviceCategory: "Service",
      serviceCategoryDescription: "Service",
      shouldCollectMoney: false,
      amountToCollect: 0,
    }));
    setRentItemsLocal(
      (erfData.rentItems || []).map((item) => ({
        ...item,
        serviceAction: "",
      })),
    );
    setSelectedSeId("");
    setSelectedSeData(null);
  }, [show, erfData]);

  // Handlers
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleServiceActionChange = (index, value) => {
    setRentItemsLocal((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], serviceAction: value };
      return updated;
    });
  };

  const handleSeChange = (e) => {
    const id = e.target.value;
    setSelectedSeId(id);
    const found = (mrUserData?.data || []).find((u) => u.userId === id);
    setSelectedSeData(found || null);
  };

  const handleFileUpload = (data) =>
    setFormData((prev) => ({ ...prev, attachmentId: data.attachmentId }));

  // Submit
  const handleSubmit = async () => {
    if (!selectedSeData?.userId) return toast.error("Select Service Executive");
    if (!formData.serviceDate) return toast.error("Select service date");

    const serviceRequiredItems = buildServiceRequiredItems(rentItemsLocal);
    if (serviceRequiredItems.length === 0)
      return toast.error("Select at least one item that needs service");

    if (formData.shouldCollectMoney) {
      if (Number(formData.amountToCollect) <= 0)
        return toast.error("Enter amount to collect");
      if (Number(formData.taxPercentage) < 0)
        return toast.error("GST % cannot be negative");
    }

    setLoading(true);
    try {
      const { activeDocumentNr, activeDocumentType, rootErfNr, parentExtNr } =
        getActiveDocument(erfData);

      const currentUser = {
        userId: userData?.data?.userId,
        name: userData?.data?.firstName || userData?.data?.name || "",
      };

      const seEntry = buildSeAssignment(
        selectedSeData,
        formData.serviceCategory,
        formData.serviceCategoryDescription,
        0,
        currentUser,
      );

      // STEP 1: Create service record (full payload)
      const addPayload = {
        data: {
          equipmentRentNr: activeDocumentNr, // current doc (ERF or Extension)
          equipmentNr: rootErfNr, // always the base ERF
          equipmentExtNr: parentExtNr || "",
          activeDocumentNr,
          activeDocumentType,
          parentExtNr,
          erfTypeCode: erfData.erfTypeCode || "",
          erfType: erfData.erfType || "",

          // Patient snapshot — full details
          patientName: formData.patientName,
          age: formData.age,
          mobileNr: formData.patientNo,
          email: formData.emailId,
          billingAddress: formData.billingAddress,
          deliveryAddress: formData.deliveryAddress,
          salePoint: formData.salePoint,
          costCenter: formData.costCentre,
          caregiverName: formData.caregiverName,
          caregiverMobileNr: formData.caregiverNo1,
          caregiverNo2: formData.caregiverNo2,
          caregiverRelation: formData.caregiverRelation,

          // Service form
          serviceDate: formData.serviceDate,
          serviceTime: formData.serviceTime,
          // servicePassTo: formData.servicePassTo,
          deliveryPassTo: formData.deliveryPassTo,
          orderType: formData.orderType,
          challenges: formData.challenges,
          attachmentId: formData.attachmentId,
          serviceCategory: formData.serviceCategory,
          serviceCategoryDescription: formData.serviceCategoryDescription,

          serviceRequiredItems,

          // Same key/structure as ERF
          assignedServiceExecutives: [seEntry],
          serviceProviderCode: seEntry.serviceProviderCode,
          serviceProviderUserId: seEntry.serviceProviderUserId,
          serviceProviderName: seEntry.serviceProviderName,

          // Header-level money
          shouldCollectMoney: formData.shouldCollectMoney,
          amountToCollect: previewGross,
          taxPercentage: previewTaxPct,
          serviceNetTotal: previewNet,
          serviceTaxTotal: previewTax,
          serviceGrossTotal: previewGross,
          totalCollected: 0,
          companyNr: erfData?.companyNr || "",
          billingState: erfData?.billingState || "",
          stateCode: erfData?.stateCode || "",

          paymentCollections: [],

          // Status
          serviceStatus: SERVICE_STATUS.ASSIGNED,
          jobDoneStatus: JOB_STATUS.PENDING,
          financeStatus: FINANCE_STATUS.PENDING,
          tallyStatusIndicator: "Pending",
          tallyHistory: [],
        },
        timeZone: "Asia/Calcutta",
      };

      const addRes = await ItemService.addErfService(addPayload);
      const insertedId = addRes?.data?.insertedId;

      const payload = {
        queryName: "ERF_Service_Detail",
        equipmentRentNr: activeDocumentNr,
        page: 1,
        per_page: 1000,
        timeZone: "Asia/Calcutta",
      };

      const latestRes = await ItemService.generateQuery(payload);
      const list = latestRes?.data || latestRes?.data?.data || [];

      // Deterministic: find by insertedId. Fallback to last element if not found.
      const justCreated =
        list.find((r) => r._id === insertedId) || list[list.length - 1];

      const newServiceNr =
        justCreated?.erfServiceNumber || justCreated?.documentNumber || "";

      // STEP 2: Update ERF — only changed keys
      const newServiceActionStatus =
        computePcCallStatusOnCreate(serviceRequiredItems);
      const erfUpdatePayload = {
        documentNumber: erfData.documentNumber,
        equipmentRentNr: erfData.documentNumber,
        serviceAction: newServiceActionStatus,
        latestServiceNr: newServiceNr,
        currentServiceJobStatus: JOB_STATUS.PENDING,
        rentItems: rentItemsLocal,
        // Append SE without amount details (header-level on service record)
        assignedServiceExecutives: [
          ...(erfData.assignedServiceExecutives || []),
          {
            version: `v${(erfData.assignedServiceExecutives?.length || 0) + 1}`,
            serviceProviderCode: selectedSeData.userId,
            serviceProviderUserId: selectedSeData.userId,
            serviceProviderName:
              selectedSeData.firstName || selectedSeData.name || "",
            assignDateAndTime: new Date().toISOString(),
            serviceAssignCategory: formData.serviceCategory,
            jobStatus: JOB_STATUS.PENDING,
            linkedServiceNr: newServiceNr,
          },
        ],
      };

      // await ItemService.updateEquipmentRentForm(erfUpdatePayload);
      const isExtensionDoc = activeDocumentType === "Extension";
      if (isExtensionDoc) {
        await ItemService.updateExtendEquipmentRent(erfUpdatePayload);
      } else {
        await ItemService.updateEquipmentRentForm(erfUpdatePayload);
      }

      toast.success("Service created successfully");
      onSuccess?.();
      onHide();
    } catch (err) {
      console.error("Service create error:", err);
      toast.error(err?.response?.data?.message || "Failed to create service");
    } finally {
      setLoading(false);
    }
  };

  // Rent items table
  const rentColumns = [
    {
      name: "Mcode / Description",
      cell: (row) => (
        <div>
          <div className="p-1 fw-bold">{row.materialCode || "--"}</div>
          <div className="m-1">{row.materialDescription || "--"}</div>
        </div>
      ),
      width: "180px",
    },
    {
      name: "Barcode",
      cell: (row) => <span>{row.barcode || "--"}</span>,
      width: "160px",
    },
    { name: "Qty", selector: (r) => r.orderedQuantity || "--", width: "70px" },
    { name: "Days", selector: (r) => r.days || "--", width: "70px" },
    { name: "From", selector: (r) => r.fromDate || "--", width: "110px" },
    { name: "To", selector: (r) => r.toDate || "--", width: "110px" },
    { name: "Amount", selector: (r) => r.grossValue || "--", width: "100px" },
    {
      name: "Service Action",
      cell: (row, index) => (
        <select
          className="form-control form-control-sm"
          value={row.serviceAction || ""}
          onChange={(e) => handleServiceActionChange(index, e.target.value)}
          style={{ minWidth: 150 }}
        >
          {SERVICE_ACTION_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ),
      width: "180px",
    },
  ];

  const selectedItemCount = rentItemsLocal.filter(
    (r) => r.serviceAction && r.serviceAction !== "",
  ).length;

  // Live tax breakdown — computed from what dispatch manager types
  const previewGross = Number(formData.amountToCollect) || 0;
  const previewTaxPct = Number(formData.taxPercentage) || 18;
  const previewTax = parseFloat(
    ((previewGross * previewTaxPct) / (100 + previewTaxPct)).toFixed(2),
  );
  const previewNet = parseFloat((previewGross - previewTax).toFixed(2));

  return (
    <Modal show={show} onHide={onHide} size="xl" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className="bi bi-tools" />
            </div>
            <div>
              <div>Create Service</div>
              {erfData?.documentNumber && (
                <div>ERF: {erfData.documentNumber}</div>
              )}
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{
          maxHeight: "80vh",
          overflowY: "auto",
          padding: "16px",
        }}
      >
        {/* ACC 1 — Service / Patient form */}
        <div
          className="accordion mt-1"
          style={{
            borderRadius: 10,
            overflow: "hidden",
            marginBottom: 12,
          }}
        >
          <div className="accordion-item">
            <AccordionHeader
              iconLabel="SR"
              title="Service / Replacement Form"
              subtitle="Patient & Caregiver Details"
              isOpen={isFormOpen}
              onToggle={() => setIsFormOpen((p) => !p)}
            />
            {isFormOpen && (
              <div className="accordion-body">
                <Row className="mb-3">
                  {[
                    { label: "ERF No", name: "srNo", disabled: true },
                    { label: "Date", name: "serviceDate", type: "date" },
                    { label: "Time", name: "serviceTime", type: "time" },
                    { label: "Sale Point", name: "salePoint" },
                    { label: "Delivery Pass To", name: "deliveryPassTo" },
                    { label: "Patient's Name", name: "patientName" },
                    { label: "Age", name: "age", type: "number" },
                    { label: "Patient No.", name: "patientNo" },
                    { label: "Email Id", name: "emailId" },
                  ].map(({ label, name, type, disabled }) => (
                    <Col
                      key={name}
                      lg={3}
                      md={6}
                      className="commTopButtonRightLeftPadding"
                    >
                      <InputField
                        className="inputBox"
                        label={label}
                        name={name}
                        type={type || "text"}
                        value={formData[name] || ""}
                        onChange={handleFormChange}
                        disabled={disabled}
                      />
                    </Col>
                  ))}
                </Row>
                <hr className="my-3" />
                <Row className="mb-3">
                  {/* <Col lg={3} md={6} className="commTopButtonRightLeftPadding">
                    <label className="form-label">Service Pass To</label>
                    <select
                      className="form-control inputBox"
                      name="servicePassTo"
                      value={formData.servicePassTo}
                      onChange={handleFormChange}
                    >
                      <option value="">Select...</option>
                      {SERVICE_PASS_TO_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </Col> */}
                  {[
                    { label: "User Name", name: "userName" },
                    { label: "ERF Type", name: "erfType" },
                    { label: "ERF No", name: "reno" },
                    { label: "Order Type", name: "orderType" },
                    { label: "ERF Date", name: "erfDate", type: "date" },
                    { label: "Cost Centre", name: "costCentre" },
                    { label: "Caregiver's Name", name: "caregiverName" },
                    { label: "Relationship", name: "caregiverRelation" },
                    { label: "Caregiver No. 1", name: "caregiverNo1" },
                    { label: "Caregiver No. 2", name: "caregiverNo2" },
                  ].map(({ label, name, type }) => (
                    <Col
                      key={name}
                      lg={3}
                      md={6}
                      className="commTopButtonRightLeftPadding"
                    >
                      <InputField
                        className="inputBox"
                        label={label}
                        name={name}
                        type={type || "text"}
                        value={formData[name] || ""}
                        onChange={handleFormChange}
                      />
                    </Col>
                  ))}
                </Row>
                <hr className="my-3" />
                <Row className="mb-3">
                  {[
                    {
                      label: "Billing Address",
                      name: "billingAddress",
                      icon: "bi-geo-alt text-primary",
                      cols: 6,
                    },
                    {
                      label: "Delivery Address",
                      name: "deliveryAddress",
                      icon: "bi-truck text-success",
                      cols: 6,
                    },
                    {
                      label: "Challenges",
                      name: "challenges",
                      icon: "bi-exclamation-circle text-warning",
                      cols: 12,
                    },
                  ].map(({ label, name, icon, cols }) => (
                    <Col
                      key={name}
                      lg={cols}
                      md={12}
                      className="commTopButtonRightLeftPadding mt-2"
                    >
                      <label className="form-label fw-semibold mb-2">
                        <i className={`bi ${icon} me-1`} />
                        {label}
                      </label>
                      <textarea
                        className="form-control inputBox"
                        name={name}
                        value={formData[name] || ""}
                        onChange={handleFormChange}
                        rows={3}
                        style={{ resize: "vertical" }}
                      />
                    </Col>
                  ))}
                </Row>
                <hr className="my-3" />
                <Row>
                  <Col lg={6} md={12} className="commTopButtonRightLeftPadding">
                    <FileUploadComponent
                      id="serviceFormAttachment"
                      name="serviceFormAttachment"
                      label="Attach Supporting Document"
                      allowedTypes={[
                        "image/png",
                        "image/jpeg",
                        "application/pdf",
                      ]}
                      multiple={false}
                      onSuccessUpload={handleFileUpload}
                      value={formData.attachmentId || ""}
                    />
                  </Col>
                </Row>
              </div>
            )}
          </div>
        </div>

        {/* ACC 2 — Items */}
        <div
          className="accordion"
          style={{
            borderRadius: 10,
            overflow: "hidden",
            marginBottom: 12,
          }}
        >
          <div className="accordion-item">
            <AccordionHeader
              iconLabel="RD"
              title="Rent Product Details"
              subtitle={`Select items needing service (${selectedItemCount} selected of ${rentItemsLocal.length})`}
              isOpen={isItemsOpen}
              onToggle={() => setIsItemsOpen((p) => !p)}
            />
            {isItemsOpen && (
              <div className="accordion-body p-0">
                {rentItemsLocal.length === 0 ? (
                  <div className="p-4 text-center text-muted">
                    <i className="bi bi-inbox fs-4 d-block mb-2" />
                    No rent items
                  </div>
                ) : (
                  <DataTable
                    columns={rentColumns}
                    data={rentItemsLocal}
                    striped
                    pagination
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* ACC 3 — Money */}
        <div
          className="accordion"
          style={{
            borderRadius: 10,
            overflow: "hidden",
            marginBottom: 12,
          }}
        >
          <div className="accordion-item">
            <AccordionHeader
              iconLabel="MC"
              title="Money Collection"
              subtitle="Should SE collect money from patient?"
              isOpen={isMoneyOpen}
              onToggle={() => setIsMoneyOpen((p) => !p)}
            />
            {isMoneyOpen && (
              <div className="accordion-body">
                <Row>
                  <Col lg={6} md={6} className="commTopButtonRightLeftPadding">
                    <div className="form-check d-flex align-items-center gap-2 mt-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="shouldCollectMoney"
                        name="shouldCollectMoney"
                        checked={formData.shouldCollectMoney}
                        onChange={handleFormChange}
                        style={{ width: 20, height: 20 }}
                      />
                      <label
                        className="form-check-label fw-bold mt-1"
                        htmlFor="shouldCollectMoney"
                      >
                        Requires Payment Collection
                      </label>
                    </div>
                  </Col>
                  {formData.shouldCollectMoney && (
                    <>
                      <Col
                        lg={4}
                        md={6}
                        className="commTopButtonRightLeftPadding"
                      >
                        <InputField
                          className="inputBox"
                          label="Amount to Collect — INR (incl. GST)"
                          name="amountToCollect"
                          type="number"
                          value={formData.amountToCollect}
                          onChange={handleFormChange}
                        />
                      </Col>
                      <Col
                        lg={2}
                        md={4}
                        className="commTopButtonRightLeftPadding"
                      >
                        <InputField
                          className="inputBox"
                          label="GST %"
                          name="taxPercentage"
                          type="number"
                          value={formData.taxPercentage}
                          onChange={handleFormChange}
                        />
                      </Col>
                    </>
                  )}
                  {formData.shouldCollectMoney && (
                    <>
                      <Col
                        xs={12}
                        md={4}
                        lg={3}
                        className="commTopButtonRightLeftPadding mt-2"
                      >
                        <InputField
                          className="inputBox"
                          label="Net Amount (excl. GST)"
                          value={previewNet.toFixed(2)}
                          disabled={true}
                        />
                      </Col>
                      <Col
                        xs={12}
                        md={4}
                        lg={3}
                        className="commTopButtonRightLeftPadding mt-2"
                      >
                        <InputField
                          className="inputBox"
                          label={`GST Amount (${previewTaxPct}%)`}
                          value={previewTax.toFixed(2)}
                          disabled={true}
                        />
                      </Col>
                      <Col
                        xs={12}
                        md={4}
                        lg={3}
                        className="commTopButtonRightLeftPadding mt-2"
                      >
                        <InputField
                          className="inputBox"
                          label="Gross Total (incl. GST)"
                          value={previewGross.toFixed(2)}
                          disabled={true}
                        />
                      </Col>
                    </>
                  )}
                </Row>
              </div>
            )}
          </div>
        </div>

        {/* ACC 4 — Assign SE */}
        <div
          className="accordion"
          style={{
            borderRadius: 10,
            overflow: "hidden",
            marginBottom: 12,
          }}
        >
          <div className="accordion-item">
            <AccordionHeader
              iconLabel="SE"
              title="Assign Service Executive"
              subtitle="Pick SE and category"
              isOpen={isAssignOpen}
              onToggle={() => setIsAssignOpen((p) => !p)}
            />
            {isAssignOpen && (
              <div className="accordion-body">
                <Row>
                  <Col lg={4} md={6} className="commTopButtonRightLeftPadding">
                    <label className="form-label">
                      Service Executive <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control inputBox"
                      value={selectedSeId}
                      onChange={handleSeChange}
                    >
                      <option value="">— Select Service Executive —</option>
                      {(mrUserData?.data || []).map((u) => (
                        <option key={u.userId} value={u.userId}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                  </Col>
                  <Col lg={4} md={6} className="commTopButtonRightLeftPadding">
                    <InputField
                      className="inputBox"
                      label="SE Code"
                      name="seCode"
                      value={selectedSeData?.userId || ""}
                      disabled
                    />
                  </Col>
                  <Col lg={4} md={6} className="commTopButtonRightLeftPadding">
                    <InputField
                      className="inputBox"
                      label="SE Name"
                      name="seName"
                      value={
                        selectedSeData?.firstName || selectedSeData?.name || ""
                      }
                      disabled
                    />
                  </Col>
                </Row>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Row className="my-4">
          <Col className="d-flex justify-content-end gap-3">
            <button
              className="btnTable btn-danger"
              onClick={onHide}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <i className="bi bi-x-circle" />
              Cancel
            </button>
            <button
              className="btnTable"
              onClick={handleSubmit}
              disabled={loading}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <i className="bi bi-check-circle" />
              {loading ? "Creating..." : "Create Service"}
            </button>
          </Col>
        </Row>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateServiceModal;
